import { isMobileApp } from "common/utils/browser";
import { useDesignOptions } from "../contexts/DesignOptionsContext";
import { DesignOptions } from "../DesignOptions";
import { ErrorMessage } from "../ErrorMessage";
import { FileUpload } from "../FileUpload";
import { MobileBottomSheet } from "../MobileBottomSheet";
import { PromptInput } from "../PromptInput";
import type { GeneratedImage, UploadResponse } from "../types";
import { ImagePreview } from "./ImagePreview";

type AppLayoutProps = {
  // Image generation state
  generatedImages: GeneratedImage[];
  isGenerating: boolean;
  selectingImageUrl: string | null;
  error: string | null;

  // Prompt state
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onGenerate: () => void;

  // Image actions
  onSelectImage: (imageUrl: string) => void;
  onStartEditing: (index: number) => void;
  onStartEditingUploadedImage: (imageUrl: string) => void;

  // Mobile state
  isMobile: boolean;
  isMobileMenuOpen: boolean;
  onOpenMobileMenu: () => void;
  onCloseMobileMenu: () => void;

  // Error handling
  onErrorDismiss: () => void;
  onUploadError: (error: string) => void;
};

export const AppLayout = ({
  generatedImages,
  isGenerating,
  selectingImageUrl,
  error,
  prompt,
  onPromptChange,
  onGenerate,
  onSelectImage,
  onStartEditing,
  onStartEditingUploadedImage,
  isMobile,
  isMobileMenuOpen,
  onOpenMobileMenu,
  onCloseMobileMenu,
  onErrorDismiss,
  onUploadError
}: AppLayoutProps) => {
  const {
    selectedTemplate,
    selectedStyle,
    selectedOrientation,
    selectedColors,
    setSelectedTemplate,
    setSelectedStyle,
    setSelectedOrientation,
    setSelectedColors
  } = useDesignOptions();

  const handleUploadSuccess = (uploadResponse: UploadResponse) => {
    if (uploadResponse.success && uploadResponse.ai_image?.image_url) {
      onStartEditingUploadedImage(uploadResponse.ai_image.image_url);
    }
  };

  return (
    <div
      className={`${isMobileApp() ? "h-[calc(100vh-64px)]" : ""} flex overflow-hidden bg-[#F9FAFC] text-gray-900`}
    >
      {/* Main Content Area */}
      <div className="flex flex-1 flex-col">
        {/* Error Message */}
        {error && <ErrorMessage error={error} onDismiss={onErrorDismiss} />}

        {/* Preview Area */}
        <div className={`flex-1 ${isMobile ? "p-4" : "p-6"} overflow-y-auto bg-gray-50`}>
          <ImagePreview
            generatedImages={generatedImages}
            isGenerating={isGenerating}
            selectedOrientation={selectedOrientation}
            onSelectImage={onSelectImage}
            onStartEditing={onStartEditing}
            selectingImageUrl={selectingImageUrl}
            isMobile={isMobile}
          />
        </div>

        {/* Prompt Input */}
        <PromptInput
          prompt={prompt}
          onPromptChange={onPromptChange}
          onGenerate={onGenerate}
          isGenerating={isGenerating}
          isMobile={isMobile}
          {...(isMobile && { onOpenMobileMenu })}
        />

        {/* File Upload Section */}
        <div className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"} bg-white`}>
          <FileUpload
            onUploadSuccess={handleUploadSuccess}
            onUploadError={onUploadError}
            clearError={onErrorDismiss}
            isMobile={isMobile}
          />
        </div>
        {/* Bottom padding for development banner on mobile */}
        {isMobileApp() && <div className="h-[82px]"></div>}
      </div>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div
          className="w-72 overflow-y-auto bg-white p-4"
          role="region"
          aria-label="Design options sidebar"
          data-testid="desktop-sidebar"
        >
          <DesignOptions
            selectedTemplate={selectedTemplate}
            selectedStyle={selectedStyle}
            selectedOrientation={selectedOrientation}
            selectedColors={selectedColors}
            onTemplateChange={setSelectedTemplate}
            onStyleChange={setSelectedStyle}
            onOrientationChange={setSelectedOrientation}
            onColorsChange={setSelectedColors}
          />
        </div>
      )}

      {/* Mobile Bottom Sheet */}
      {isMobile && (
        <MobileBottomSheet
          isOpen={isMobileMenuOpen}
          onClose={onCloseMobileMenu}
          selectedTemplate={selectedTemplate}
          selectedStyle={selectedStyle}
          selectedOrientation={selectedOrientation}
          selectedColors={selectedColors}
          onTemplateChange={setSelectedTemplate}
          onStyleChange={setSelectedStyle}
          onOrientationChange={setSelectedOrientation}
          onColorsChange={setSelectedColors}
        />
      )}
    </div>
  );
};
